import React, { useState } from "react";
import moment from "moment";
import baseUrl from "../../../utils/baseUrl";
import { Modal } from "antd"; // Import Modal from Ant Design
import {
  IoArrowForward,
  IoPersonOutline,
  IoTimeOutline,
  IoCalendarOutline,
  IoCheckmarkCircle,
} from "react-icons/io5";
import { FaThumbsUp, FaRegThumbsUp } from "react-icons/fa";

// Rough plain-text length, used only to decide whether a "Read more" affordance
// is worth showing — doesn't need to be exact.
const stripHtml = (html) => html.replace(/<[^>]*>/g, "");

const TicketCard = ({
  ticket,
  canRemind,
  isReminded,
  onToggleReminder,
  likeCount = 0,
  isLiked,
  onToggleLike,
}) => {
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null); // null = lightbox closed

  const formattedDate = moment(ticket.createdAt).format("MMM D, YYYY");
  const formattedTime = moment(ticket.createdAt).format("hh:mm A");

  // Check if the ticket is less than 4 hours old
  const isNew = moment().diff(moment(ticket.createdAt), "hours") < 4;

  const customerName = `${ticket.customer?.firstName || "Unknown"} ${
    ticket.customer?.lastName || ""
  }`.trim();

  const images = ticket.images || [];
  const description = ticket.description || "";
  const isLong = stripHtml(description).length > 200;
  // The department tag stays short even when the full department name has a
  // parenthetical spelled out (e.g. "IIPG (Institute of ...)") — just the
  // short form before the "(" is shown; the full name still appears
  // wherever else it's used (e.g. the IIPG author name).
  const departmentName = (ticket.department?.name || "General").split(" (")[0].trim();

  const openImage = (event, imageUrl) => {
    event.stopPropagation(); // don't also open the post detail behind it
    setSelectedImage(imageUrl);
  };

  // The event date is a plain calendar date (stored as UTC midnight), so it's
  // read back in UTC to avoid drifting a day in other time zones.
  const eventDateLabel = ticket.eventDate ? moment.utc(ticket.eventDate).format("MMM D, YYYY") : null;

  // Only posts that actually have an event date can be added to a calendar,
  // and only for signed-in users on the members-only tabs.
  const reminderButton =
    canRemind && eventDateLabel ? (
      <button
        type="button"
        className={`reminder-btn${isReminded ? " is-reminded" : ""}`}
        onClick={(event) => {
          event.stopPropagation(); // don't also open the post detail behind it
          onToggleReminder?.(ticket);
        }}
        aria-label={isReminded ? "Remove from Google Calendar" : "Add to Google Calendar"}
        title={
          isReminded
            ? "Remove this event from your Google Calendar"
            : "Add this event to your Google Calendar"
        }
      >
        {isReminded ? <IoCheckmarkCircle /> : <IoCalendarOutline />}
      </button>
    ) : null;

  // Everyone sees the total; clicking needs a signed-in user (the feed page
  // shows a sign-in hint otherwise).
  const likeButton = (
    <div className="post-actions">
      <button
        type="button"
        className={`like-btn${isLiked ? " is-liked" : ""}`}
        onClick={(event) => {
          event.stopPropagation(); // don't also open the post detail behind it
          onToggleLike?.(ticket);
        }}
        aria-pressed={Boolean(isLiked)}
        aria-label={
          `${isLiked ? "Unlike" : "Like"} this post` +
          (likeCount > 0 ? ` (${likeCount} ${likeCount === 1 ? "like" : "likes"})` : "")
        }
        title={isLiked ? "Unlike" : "Like"}
      >
        {isLiked ? <FaThumbsUp /> : <FaRegThumbsUp />}
        {likeCount > 0 && <span className="like-count">{likeCount}</span>}
      </button>
    </div>
  );

  // Author on the left, like on the right — one tidy footer row.
  const userFooter = (
    <div className="user">
      <span className="user-avatar">
        <IoPersonOutline />
      </span>
      <div className="user-info">
        <h5>{customerName || "Unknown"}</h5>
        <small>
          <IoTimeOutline /> {formattedDate} at {formattedTime}
        </small>
      </div>
      {likeButton}
    </div>
  );

  const eventDateLine = eventDateLabel ? (
    <p className="event-date-line">
      <IoCalendarOutline />
      <strong>Event Date: {eventDateLabel}</strong>
    </p>
  ) : null;

  return (
    <div className="post-card">
      {/* Everything in here opens the full post; the enlarge-image lightbox and
          the detail modal below are siblings, not children, of this wrapper,
          so clicks inside those (portaled) modals never bubble back into it. */}
      <div
        className="post-card-clickable"
        role="button"
        tabIndex={0}
        onClick={() => setIsDetailOpen(true)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") setIsDetailOpen(true);
        }}
      >
        {images.length > 0 && (
          <div className={`post-card-media${images.length > 1 ? " multi" : ""}`}>
            {images.map((image, index) => (
              <img
                key={index}
                src={`${baseUrl}/files/${image.imageName}`}
                alt={`Attachment ${index + 1}`}
                onClick={(event) => openImage(event, `${baseUrl}/files/${image.imageName}`)}
              />
            ))}
          </div>
        )}
        <div className="card-body">
          <div className="card-body-top">
            <div className="card-body-top-left">
              {reminderButton}
              {isNew && <span className="new-label">New</span>}
            </div>
            <span className="tag-department">{departmentName}</span>
          </div>
          <h4 className="title">{ticket.subject}</h4>
          {eventDateLine}
          <div className={`content content-preview${isLong ? " has-more" : ""}`}>
            <div dangerouslySetInnerHTML={{ __html: description }} />
          </div>
          {isLong && (
            <span className="read-more">
              Read full post <IoArrowForward />
            </span>
          )}
          {userFooter}
        </div>
      </div>

      {/* Full post detail */}
      <Modal
        open={isDetailOpen}
        onCancel={() => setIsDetailOpen(false)}
        footer={null}
        width={700}
        className="post-detail-modal"
      >
        {images.length > 0 && (
          <div className={`post-card-media${images.length > 1 ? " multi" : ""}`}>
            {images.map((image, index) => (
              <img
                key={index}
                src={`${baseUrl}/files/${image.imageName}`}
                alt={`Attachment ${index + 1}`}
                onClick={(event) => openImage(event, `${baseUrl}/files/${image.imageName}`)}
              />
            ))}
          </div>
        )}
        <div className="card-body">
          <div className="card-body-top">
            <div className="card-body-top-left">
              {reminderButton}
              {isNew && <span className="new-label">New</span>}
            </div>
          </div>
          <h4 className="title">{ticket.subject}</h4>
          {eventDateLine}
          <div className="content" dangerouslySetInnerHTML={{ __html: description }} />
          {userFooter}
        </div>
      </Modal>

      {/* Enlarged image */}
      <Modal
        open={Boolean(selectedImage)}
        onCancel={() => setSelectedImage(null)}
        footer={null}
        width="80%"
        style={{ maxWidth: "800px" }}
        className="image-lightbox"
        zIndex={1050}
        closeIcon={
          <span
            style={{
              color: "#fff",
              fontSize: "30px",
              fontWeight: "bold",
            }}
          >
            ×
          </span>
        }
      >
        <img src={selectedImage} alt="Enlarged" />
      </Modal>
    </div>
  );
};

export default TicketCard;
